"use client";

import { useEffect, useLayoutEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuthenticatedUserQuery } from "@/features/auth/api/profile-queries";
import type { ROLES } from "@/features/auth/api/types";
import { ensureAuthenticated, isAuthenticated } from "@/lib/auth-utils";
import { CenteredPageLoader } from "@/shared/ui";
export interface AuthGuardProps {
  children: React.ReactNode;
  /** When set, also requires the user's accountType to be one of these roles. */
  allowedRoles?: readonly ROLES[];
  /** Where unauthorized (role) users are sent. Defaults to "/". */
  redirectTo?: string;
}

/**
 * Client-side guard for protected routes.
 * 1. Authentication — valid tokens (sync localStorage check, then async refresh).
 * 2. Authorization (optional) — accountType must be in `allowedRoles`.
 */
export function AuthGuard({
  children,
  allowedRoles,
  redirectTo = "/"
}: AuthGuardProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isAuthenticatedState, setIsAuthenticatedState] = useState(false);
  const shouldCheckRole = Boolean(allowedRoles?.length && isAuthenticatedState);
  const {
    data: userInfo,
    isLoading: isRoleLoading
  } = useAuthenticatedUserQuery(shouldCheckRole);
  const isAuthorized = !allowedRoles?.length || Boolean(userInfo && allowedRoles.includes(userInfo.accountType));

  // Synchronous check before paint — avoids fullScreen loader flash on refresh.
  // Starts false to match SSR; reading localStorage in useLayoutEffect avoids a
  // hydration mismatch while still updating before the browser paints.
  useLayoutEffect(() => {
    if (isAuthenticated()) {
      setIsAuthenticatedState(true);
    }
  }, [setIsAuthenticatedState]);

  // Handle BF Cache (Back-Forward Cache) restores: when the browser restores a cached
  // page snapshot after pressing the back button, React state is preserved from when
  // the user was logged in. This listener catches that case and forces a re-auth check.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted && !isAuthenticated()) {
        router.replace("/login");
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [router]);
  useEffect(() => {
    // If synchronous check passed (valid, non-expired tokens), skip async check
    if (isAuthenticatedState) return;
    const checkAuth = async () => {
      // Note: Auto-refresh is now initialized globally in SessionProvider
      // This ensures it persists across navigation

      // Unified check with automatic token refresh
      const authenticated = await ensureAuthenticated();
      if (!authenticated) {
        // Store current path to return after login (but not if already on login page)
        if (pathname !== "/login") {
          localStorage.setItem("return_url", pathname);
        } else {
          // Clear any stale return_url if we're on login page
          localStorage.removeItem("return_url");
        }
        // Use replace so the protected page is removed from the browser history stack,
        // preventing the back button from returning to it after a login redirect.
        router.replace("/login");
      } else {
        setIsAuthenticatedState(true);
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  useEffect(() => {
    if (!shouldCheckRole || isRoleLoading || isAuthorized) return;
    router.replace(redirectTo);
  }, [shouldCheckRole, isRoleLoading, isAuthorized, redirectTo, router]);
  if (!isAuthenticatedState) {
    return <CenteredPageLoader fullScreen />;
  }
  if (shouldCheckRole) {
    if (isRoleLoading) {
      return <CenteredPageLoader fullScreen />;
    }
    if (!isAuthorized) {
      return null;
    }
  }
  return <>{children}</>;
}